import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { X, Download, CheckSquare, Square, Calendar, FileSpreadsheet, Loader2, Search } from 'lucide-react';
import * as XLSX from 'xlsx-js-style';
import { useAuth } from '../contexts/AuthContext';
import { logUsageEvent } from '../lib/logger';

export interface ExportColumn {
    key: string;
    label: string;
    defaultSelected?: boolean;
    /** Cor do cabeçalho na planilha (hex sem #), para destacar um grupo de colunas. */
    headerColor?: string;
    format?: (value: any, item: any) => string | number | Date | { v: any, t: string, z?: string };
}

interface ExportModalProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    columns: ExportColumn[];
    data: any[];
    filename: string;
    dateField?: string;
    dateLabel?: string;
    availableDateFields?: { key: string; label: string }[];
    summary?: { label: string; value: string | number }[];
}

const ExportModal: React.FC<ExportModalProps> = ({
    isOpen,
    onClose,
    title,
    columns,
    data,
    filename,
    dateField = 'created_at',
    dateLabel = 'Data de Cadastro',
    availableDateFields,
    summary
}) => {
    const { user } = useAuth();
    const [selectedColumns, setSelectedColumns] = useState<Set<string>>(
        () => new Set(columns.filter(c => c.defaultSelected !== false).map(c => c.key))
    );
    const [activeDateField, setActiveDateField] = useState(
        availableDateFields?.[0]?.key || dateField || 'created_at'
    );
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [selectedMeses, setSelectedMeses] = useState<Set<string>>(new Set());
    const [exporting, setExporting] = useState(false);
    const [columnSearch, setColumnSearch] = useState('');

    const mesesDisponiveis = useMemo(() => {
        const unique = Array.from(new Set(data.map(item => item.mes_referencia).filter(Boolean)));
        const monthOrder: Record<string, number> = {
            'JAN': 1, 'FEV': 2, 'MAR': 3, 'ABR': 4, 'MAI': 5, 'JUN': 6,
            'JUL': 7, 'AGO': 8, 'SET': 9, 'OUT': 10, 'NOV': 11, 'DEZ': 12
        };

        const grouped = unique.reduce((acc: Record<string, any[]>, mesAno: string) => {
            const parts = mesAno.split('/');
            if (parts.length === 2) {
                const [mes, ano] = parts;
                if (!acc[ano]) acc[ano] = [];
                acc[ano].push({ label: mesAno, mes, ano, mesNum: monthOrder[mes.toUpperCase()] || 0 });
            }
            return acc;
        }, {});

        const sortedYears = Object.keys(grouped).sort((a, b) => Number(a) - Number(b));

        return sortedYears.map(ano => {
            grouped[ano].sort((a, b) => a.mesNum - b.mesNum);
            return { ano, meses: grouped[ano] };
        });
    }, [data]);

    const toggleMes = (mesAno: string) => {
        setSelectedMeses(prev => {
            const next = new Set(prev);
            if (next.has(mesAno)) next.delete(mesAno);
            else next.add(mesAno);
            return next;
        });
    };

    const handleMarcarTodosMeses = () => {
        const todos = mesesDisponiveis.flatMap(g => g.meses.map(m => m.label));
        setSelectedMeses(new Set(todos));
    };

    const handleLimparMeses = () => {
        setSelectedMeses(new Set());
    };

    const allSelected = selectedColumns.size === columns.length;
    const noneSelected = selectedColumns.size === 0;

    const toggleColumn = (key: string) => {
        setSelectedColumns(prev => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const toggleAll = () => {
        if (allSelected) {
            setSelectedColumns(new Set());
        } else {
            setSelectedColumns(new Set(columns.map(c => c.key)));
        }
    };

    const filteredData = useMemo(() => {
        // Only proceed if filtered
        const hasRangeFilter = startDate || endDate;
        const hasMonthFilter = activeDateField === 'mes_referencia' && selectedMeses.size > 0;

        if (!hasRangeFilter && !hasMonthFilter) return data;

        return data.filter(item => {
            // Priority: Month selection if activeDateField is mes_referencia
            if (activeDateField === 'mes_referencia' && selectedMeses.size > 0) {
                return selectedMeses.has(item.mes_referencia);
            }

            // Standard date range filter
            const dateStr = item[activeDateField];
            if (!dateStr) return false;
            const date = new Date(dateStr);
            if (startDate) {
                const start = new Date(startDate);
                if (date < start) return false;
            }
            if (endDate) {
                const end = new Date(endDate);
                end.setHours(23, 59, 59, 999);
                if (date > end) return false;
            }
            return true;
        });
    }, [data, startDate, endDate, activeDateField, selectedMeses]);

    const handleExport = () => {
        const activeColumns = columns.filter(c => selectedColumns.has(c.key));
        if (activeColumns.length === 0) return;

        setExporting(true);

        try {
            const headers = activeColumns.map(c => c.label);

            const rows = filteredData.map(item =>
                activeColumns.map(col => {
                    const val = item[col.key];
                    if (col.format) return col.format(val, item);
                    if (val === null || val === undefined) return '';
                    return val;
                })
            );

            const wb = XLSX.utils.book_new();
            const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);

            // Style headers
            const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
            for (let col = range.s.c; col <= range.e.c; col++) {
                const cellRef = XLSX.utils.encode_cell({ r: 0, c: col });
                if (ws[cellRef]) {
                    ws[cellRef].s = {
                        font: { bold: true, sz: 12, color: { rgb: 'FFFFFF' } },
                        fill: { fgColor: { rgb: activeColumns[col]?.headerColor || '1B7A4A' } },
                        alignment: { horizontal: 'center', vertical: 'center' }
                    };
                }
            }

            // Auto-fit column widths
            const PX_PER_CHAR = 8;
            const MIN_WIDTH = 80;
            const MAX_WIDTH = 300;

            ws['!cols'] = headers.map((h, i) => {
                let maxLen = h.length;
                rows.forEach(row => {
                    const val = row[i] != null ? String(row[i]) : '';
                    if (val.length > maxLen) maxLen = val.length;
                });
                const wpx = Math.min(Math.max(Math.ceil(maxLen * PX_PER_CHAR) + 16, MIN_WIDTH), MAX_WIDTH);
                return { wpx };
            });

            const now = new Date();
            const datePart = now.toLocaleDateString('pt-BR').replace(/\//g, '-');
            const timePart = now.toLocaleTimeString('pt-BR').replace(/:/g, '');
            const timestamp = `${datePart}_${timePart}`;
            
            // Add summary sheet if provided
            if (summary && summary.length > 0) {
                const summaryWs = XLSX.utils.aoa_to_sheet([
                    ['Métrica', 'Valor'],
                    ...summary.map(s => [s.label, s.value])
                ]);
                
                // Style summary headers
                const summaryRange = XLSX.utils.decode_range(summaryWs['!ref'] || 'A1:B1');
                for (let col = summaryRange.s.c; col <= summaryRange.e.c; col++) {
                    const cellRef = XLSX.utils.encode_cell({ r: 0, c: col });
                    if (summaryWs[cellRef]) {
                        summaryWs[cellRef].s = {
                            font: { bold: true, sz: 12, color: { rgb: 'FFFFFF' } },
                            fill: { fgColor: { rgb: '1B7A4A' } },
                            alignment: { horizontal: 'center', vertical: 'center' }
                        };
                    }
                }
                
                // Auto-fit summary columns
                summaryWs['!cols'] = [
                    { wpx: Math.max(200, ...summary.map(s => String(s.label).length * PX_PER_CHAR)) },
                    { wpx: Math.max(100, ...summary.map(s => String(s.value).length * PX_PER_CHAR)) }
                ];
                
                XLSX.utils.book_append_sheet(wb, summaryWs, 'Resumo');
            }
            
            XLSX.utils.book_append_sheet(wb, ws, 'Dados');
            XLSX.writeFile(wb, `${filename}_${timestamp}.xlsx`);
            logUsageEvent({ userId: user?.id, modulo: 'financeiro', funcionalidade: 'faturas', acao: 'exportar_planilha', origem: 'acao' });
            onClose();
        } catch (error) {
            console.error('Export error:', error);
        } finally {
            setExporting(false);
        }
    };

    if (!isOpen) return null;

    return createPortal(
        <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-fade-in"
            onClick={onClose}
        >
            <div
                className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-4xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
                onClick={e => e.stopPropagation()}
            >
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center bg-gray-50 dark:bg-gray-900/50">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 rounded-lg">
                            <FileSpreadsheet size={20} />
                        </div>
                        <div>
                            <h3 className="font-bold text-lg text-gray-800 dark:text-white">{title}</h3>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                {filteredData.length} registros disponíveis
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                        <X size={20} />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-5 overflow-y-auto">
                    {/* Date/Month Filter */}
                    <div className="space-y-3">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                                <Calendar size={16} className="text-gray-400" />
                                Filtrar por Período
                            </div>
                            {availableDateFields && availableDateFields.length > 1 && (
                                <div className="flex bg-gray-100 dark:bg-gray-700/50 p-1 rounded-lg">
                                    {availableDateFields.map(field => (
                                        <button
                                            key={field.key}
                                            onClick={() => setActiveDateField(field.key)}
                                            className={`px-3 py-1 text-[10px] font-bold uppercase tracking-wider rounded-md transition-all ${
                                                activeDateField === field.key
                                                    ? 'bg-white dark:bg-gray-600 text-primary shadow-sm'
                                                    : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
                                            }`}
                                        >
                                            {field.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {activeDateField === 'mes_referencia' ? (
                            <div className="bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden flex flex-col">
                                <div className="p-2 border-b border-gray-100 dark:border-gray-700 flex justify-between gap-2 bg-white dark:bg-gray-800/50">
                                    <button
                                        onClick={handleMarcarTodosMeses}
                                        className="flex-1 px-2 py-1 text-xs font-semibold bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                                    >
                                        Todos
                                    </button>
                                    <button
                                        onClick={handleLimparMeses}
                                        className="flex-1 px-2 py-1 text-xs font-semibold bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                                    >
                                        Limpar
                                    </button>
                                </div>
                                <div className="p-3 max-h-48 overflow-y-auto">
                                    {mesesDisponiveis.map(agrupamento => (
                                        <div key={agrupamento.ano} className="mb-4 last:mb-0">
                                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2 px-1">
                                                {agrupamento.ano}
                                            </p>
                                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                                                {agrupamento.meses.map((item: any) => (
                                                    <label
                                                        key={item.label}
                                                        className={`flex items-center gap-2 px-2 py-1.5 rounded-lg border cursor-pointer transition-all ${
                                                            selectedMeses.has(item.label)
                                                                ? 'bg-primary/5 border-primary/20 text-primary'
                                                                : 'bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700 text-gray-500 hover:border-gray-200'
                                                        }`}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedMeses.has(item.label)}
                                                            onChange={() => toggleMes(item.label)}
                                                            className="w-3.5 h-3.5 rounded border-gray-300 text-primary focus:ring-primary/20"
                                                        />
                                                        <span className="text-xs font-medium truncate">{item.label}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                    {mesesDisponiveis.length === 0 && (
                                        <div className="text-center py-4 text-xs text-gray-400">
                                            Nenhum mês referência encontrado.
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="flex items-center gap-3 bg-gray-50 dark:bg-gray-900/50 p-3 rounded-xl border border-gray-200 dark:border-gray-700">
                                    <div className="flex-1">
                                        <label className="text-[10px] uppercase font-bold text-gray-400 tracking-wider">Início</label>
                                        <input
                                            type="date"
                                            value={startDate}
                                            onChange={e => setStartDate(e.target.value)}
                                            className="w-full mt-0.5 px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-200 outline-none focus:border-primary transition-colors"
                                        />
                                    </div>
                                    <span className="text-gray-400 mt-4">→</span>
                                    <div className="flex-1">
                                        <label className="text-[10px] uppercase font-bold text-gray-400 tracking-wider">Fim</label>
                                        <input
                                            type="date"
                                            value={endDate}
                                            onChange={e => setEndDate(e.target.value)}
                                            className="w-full mt-0.5 px-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-200 outline-none focus:border-primary transition-colors"
                                        />
                                    </div>
                                </div>
                                {(startDate || endDate) && (
                                    <button
                                        onClick={() => { setStartDate(''); setEndDate(''); }}
                                        className="text-xs text-primary hover:text-primary/80 font-medium transition-colors"
                                    >
                                        Limpar datas
                                    </button>
                                )}
                            </>
                        )}
                    </div>

                    {/* Column Selector */}
                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
                                <CheckSquare size={16} className="text-gray-400" />
                                Colunas para Exportar
                            </div>
                            <button
                                onClick={toggleAll}
                                className="text-xs font-medium text-primary hover:text-primary/80 transition-colors"
                            >
                                {allSelected ? 'Desmarcar Todas' : 'Selecionar Todas'}
                            </button>
                        </div>

                        <div className="relative">
                            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                            <input
                                type="text"
                                placeholder="Buscar coluna..."
                                value={columnSearch}
                                onChange={e => setColumnSearch(e.target.value)}
                                className="w-full pl-8 pr-8 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:border-primary placeholder:text-gray-400"
                            />
                            {columnSearch && (
                                <button
                                    onClick={() => setColumnSearch('')}
                                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                                >
                                    <X size={13} />
                                </button>
                            )}
                        </div>

                        <div className="bg-gray-50 dark:bg-gray-900/50 rounded-xl border border-gray-200 dark:border-gray-700 max-h-48 overflow-y-auto">
                            <div className="grid grid-cols-2 gap-px">
                                {columns
                                    .filter(col => col.label.toLowerCase().includes(columnSearch.toLowerCase()))
                                    .map((col, idx) => {
                                        const isSelected = selectedColumns.has(col.key);
                                        return (
                                            <label
                                                key={col.key}
                                                className={`flex items-center gap-2.5 px-3 py-2.5 cursor-pointer transition-colors text-sm ${
                                                    isSelected
                                                        ? 'text-gray-800 dark:text-white'
                                                        : 'text-gray-400 dark:text-gray-500'
                                                } hover:bg-white dark:hover:bg-gray-800/50 ${
                                                    idx < 2 ? 'rounded-t-lg' : ''
                                                }`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={() => toggleColumn(col.key)}
                                                    className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-primary focus:ring-primary/20 outline-none cursor-pointer"
                                                />
                                                <span className="truncate">{col.label}</span>
                                            </label>
                                        );
                                    })}
                                {columns.filter(col => col.label.toLowerCase().includes(columnSearch.toLowerCase())).length === 0 && (
                                    <div className="col-span-2 py-6 text-center text-xs text-gray-400">
                                        Nenhuma coluna encontrada
                                    </div>
                                )}
                            </div>
                        </div>

                        <p className="text-[11px] text-gray-400 dark:text-gray-500">
                            {selectedColumns.size} de {columns.length} colunas selecionadas
                        </p>
                    </div>
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between bg-gray-50/50 dark:bg-gray-900/30">
                    <p className="text-xs text-gray-400">
                        {filteredData.length} registros · {selectedColumns.size} colunas
                    </p>
                    <div className="flex gap-3">
                        <button
                            onClick={onClose}
                            className="px-5 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 font-semibold hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-sm"
                        >
                            Cancelar
                        </button>
                        <button
                            onClick={handleExport}
                            disabled={noneSelected || filteredData.length === 0 || exporting}
                            className="px-5 py-2.5 rounded-xl bg-emerald-600 text-white font-semibold hover:bg-emerald-700 transition-all shadow-lg shadow-emerald-500/20 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                        >
                            {exporting ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                            {exporting ? 'Exportando...' : 'Exportar Excel'}
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default ExportModal;
